import styled, { keyframes } from 'styled-components';

// Animations
const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

// Special animation for centered fixed elements that preserves translateX(-50%)
const fadeInCentered = keyframes`
  from {
    opacity: 0;
    transform: translateX(-50%) translateY(12px);
  }
  to {
    opacity: 1;
    transform: translateX(-50%) translateY(0);
  }
`;

const gradientShift = keyframes`
  0% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0% 50%;
  }
`;

// Token Section
export const TokenSection = styled.section`
  content-visibility: auto;
  contain-intrinsic-size: 1px 700px;
  width: 100%;
  padding: 6rem 2rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  background: linear-gradient(135deg, rgba(200, 167, 94, 0.05) 0%, rgba(26, 26, 26, 0.05) 100%);
`;

export const TokenContainer = styled.div`
  max-width: 1000px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  animation: ${fadeIn} 1s ease-out;
`;

export const TokenTitle = styled.h2`
  font-family: 'Satoshi', sans-serif;
  font-size: clamp(2rem, 5vw, 2.75rem);
  color: var(--brand-text);
  margin-bottom: 1.5rem;

  span {
    background: linear-gradient(135deg, var(--brand-gold-alt), #B8924C);
    background-size: 200% 100%;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    animation: ${gradientShift} 8s ease infinite;
    text-transform: none;
  }
`;

export const TokenText = styled.p`
  color: #666;
  line-height: 1.7;
  font-size: 1.15rem;
  margin-bottom: 3rem;
  max-width: 800px;
  text-align: center;
  width: 100%;
  margin-left: auto;
  margin-right: auto;
  max-width: clamp(32ch, 90vw, 66ch);
  text-wrap: balance;
  hyphens: auto;
`;

export const TokenButton = styled.button`
  background: linear-gradient(135deg, var(--brand-gold-alt), #B8924C);
  color: white;
  border: none;
  border-radius: 12px;
  padding: 1rem 2rem;
  font-size: 1.1rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  box-shadow: 0 5px 15px rgba(200, 167, 94, 0.15);

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 10px 20px rgba(200, 167, 94, 0.25);
    background: linear-gradient(135deg, #B8924C, #A07D3A);
  }

  svg {
    width: 24px;
    height: 24px;
  }
`;

// Capability section styling
export const CapabilitiesSection = styled.section`
  content-visibility: auto;
  contain-intrinsic-size: 1px 900px;
  width: 100%;
  padding: 5rem 2rem;
  background: linear-gradient(135deg, #FAFAFB 0%, #F5F5F5 100%);
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-image:
      radial-gradient(circle at 15% 15%, rgba(200, 167, 94, 0.04) 0%, transparent 40%),
      radial-gradient(circle at 85% 85%, rgba(26, 26, 26, 0.04) 0%, transparent 40%);
    z-index: 0;
  }
  
  @media (max-width: 768px) {
    padding: 4rem 1rem;
  }
  
  @media (max-width: 480px) {
    padding: 3rem 0.5rem;
  }
`;

// Prompt Bar styled components
export const PromptBarContainer = styled.div<{ $alignRight?: boolean }>`
  position: fixed;
  bottom: calc(var(--prompt-bar-gap) + env(safe-area-inset-bottom, 0px));
  left: ${props => props.$alignRight ? 'auto' : '50%'};
  right: ${props => props.$alignRight ? '24px' : 'auto'};
  transform: ${props => props.$alignRight ? 'translateY(0)' : 'translateX(-50%)'};
  width: 90%;
  max-width: 800px;
  background: white;
  padding: 0.75rem 1.25rem;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  border: 1px solid rgba(0,0,0,0.05);
  display: flex;
  align-items: flex-end; /* keep buttons aligned at the bottom */
  gap: 1rem;
  min-height: 56px;
  --prompt-bar-height: 56px;
  z-index: 1500;
  /* Use centered animation by default; slide-in for right-aligned */
  animation: ${props => props.$alignRight ? fadeIn : fadeInCentered} 0.35s ease-out;
  opacity: 1;
  visibility: visible;
  /* Smooth transitions for when the component is re-rendered */
  transition: opacity 0.2s ease, box-shadow 0.2s ease, transform 0.25s ease, right 0.25s ease, left 0.25s ease;
  flex-wrap: nowrap;
  overflow: visible; /* allow attachment tray to render outside */
  
  @media (max-width: 900px) {
    width: 95%;
  }
  
  @media (max-width: 600px) {
    width: calc(100% - 2rem);
    padding: 0.5rem 0.75rem;
    gap: 0.5rem;
    min-height: 48px;
  }

  /* Mobile-friendly single row (input + send) and optional attach when expanded */
  @media (max-width: 720px) {
    flex-direction: row;
    flex-wrap: nowrap;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    left: 50%;
    right: auto;
    transform: translateX(-50%);
    width: calc(100% - 2rem);
    max-width: 480px;
    padding: 0.5rem 0.75rem;
    min-height: 48px;

    & > [data-role="prompt-input"] {
      flex: 1 1 auto;
      min-width: 0;
    }

    & > button {
      flex: 0 0 auto;
      width: auto;
      min-width: 40px;
      height: 40px;
      justify-content: center;
    }
  }
`;

export const PromptInputWrapper = styled.div<{ $collapsed?: boolean }>`
  flex: 1;
  display: grid;
  min-width: 0; /* allow shrinking horizontally so buttons stay visible */
  min-height: 44px; /* single-line default */
  max-height: ${props => props.$collapsed ? '44px' : '360px'}; /* ~16 lines when expanded, single line when collapsed */

  /* Both children occupy the same grid cell */
  &::after,
  & > div {
    grid-area: 1 / 1 / 2 / 2;
    padding: 0.625rem 0.875rem;
    border: 1px solid #e2e8f0;
    border-radius: 10px;
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
    font-size: 0.95rem;
    line-height: 1.5;
    white-space: ${props => props.$collapsed ? 'nowrap' : 'pre-wrap'};
    word-wrap: break-word;
    overflow-wrap: break-word;
    box-sizing: border-box;
    width: 100%;
    min-width: 0;
    overflow-x: hidden;
    overflow-y: ${props => props.$collapsed ? 'hidden' : 'auto'};
    text-overflow: ${props => props.$collapsed ? 'ellipsis' : 'clip'};
  }
  
  /* Hidden pseudo-element mirrors textarea content to drive sizing */
  &::after {
    content: attr(data-replicated-value) ' ';
    visibility: hidden;
    pointer-events: none;
    min-height: 44px;
    max-height: ${props => props.$collapsed ? '44px' : '360px'};
  }

  @media (max-width: 720px) {
    min-height: 40px;
    max-height: ${props => props.$collapsed ? '40px' : '160px'};
    &::after,
    & > div {
      padding: 0.5rem 0.75rem;
      font-size: 0.9rem;
      min-height: 40px;
    }
    &::after {
      max-height: ${props => props.$collapsed ? '40px' : '160px'};
    }
  }
`;

export const PromptInput = styled.div`
  width: 100%;
  min-width: 0;
  min-height: 44px;
  max-height: 360px;
  resize: none;
  overflow-y: auto;
  overflow-x: hidden;
  background: #ffffff;
  transition: border-color 0.2s ease, box-shadow 0.2s ease, background 0.2s ease;
  box-sizing: border-box;
  white-space: pre-wrap;
  word-break: break-word;
  overflow-wrap: break-word;
  border: none;
  position: relative;
  direction: ltr;
  text-align: left;
  caret-color: #0f172a;
  unicode-bidi: plaintext;
  text-transform: none;
  
  &:focus {
    outline: none;
    border-color: #cbd5e1;
    box-shadow: 0 0 0 3px rgba(226, 232, 240, 0.8);
  }
  
  /* Placeholder for contentEditable */
  &:empty:before {
    content: attr(data-placeholder);
    color: #9ca3af;
    pointer-events: none;
  }
  
  /* On mobile, keep placeholder single-line with ellipsis */
  @media (max-width: 720px) {
    &:empty:before {
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      display: block;
      max-width: 100%;
    }
  }
  
  /* Custom scrollbar for expanded state */
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: #94a3b8;
  }

  @media (max-width: 720px) {
    min-height: 40px;
    max-height: 160px;
  }
`;

export const ActionButton = styled.button`
  padding: 0.5rem;
  border: 1px solid #eee;
  border-radius: 6px;
  background: white;
  color: #666;
  font-family: 'SF Pro Text', -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 0.95rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  transition: all 0.2s ease;
  height: 40px;
  width: 40px;
  
  &:hover {
    background: #f5f5f5;
    border-color: #ddd;
    color: #333;
  }

  svg {
    width: 20px;
    height: 20px;
    stroke-width: 1.75;
    opacity: 0.8;
    transition: all 0.2s ease;
  }

  &:hover svg {
    opacity: 1;
  }
`;

export const SendButton = styled(ActionButton)`
  background: #000;
  color: white;
  border: none;
  
  &:hover {
    background: #333;
    color: white;
  }
  
  svg {
    opacity: 1;
  }
`;

// Mission Section
export const MissionSection = styled.section`
  width: 100%;
  padding: 6rem 2rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  background: linear-gradient(135deg, rgba(26, 26, 26, 0.05) 0%, rgba(200, 167, 94, 0.05) 100%);
`;

export const MissionContainer = styled.div`
  max-width: 1000px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  animation: ${fadeIn} 1s ease-out;
`;

export const MissionTitle = styled.h2`
  font-family: 'Satoshi', sans-serif;
  font-size: clamp(2rem, 5vw, 2.75rem);
  color: var(--brand-text);
  margin-bottom: 1.5rem;

  span {
    background: linear-gradient(135deg, var(--brand-gold-alt), var(--brand-black));
    background-size: 200% 100%;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    animation: ${gradientShift} 8s ease infinite;
  }
`;

export const MissionText = styled.p`
  color: #666;
  line-height: 1.7;
  font-size: 1.15rem;
  margin-bottom: 3rem;
  max-width: 800px;
  text-align: center;
  width: 100%;
  margin-left: auto;
  margin-right: auto;
  max-width: clamp(32ch, 90vw, 66ch);
  text-wrap: balance;
  hyphens: auto;
`;

export const MissionButton = styled.button`
  background: linear-gradient(135deg, var(--brand-black), var(--brand-gold-alt));
  color: white;
  border: none;
  border-radius: 12px;
  padding: 1rem 2rem;
  font-size: 1.1rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  box-shadow: 0 5px 15px rgba(26, 26, 26, 0.15);

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 10px 20px rgba(26, 26, 26, 0.25);
    background: linear-gradient(135deg, var(--brand-charcoal), #B8924C);
  }

  svg {
    width: 24px;
    height: 24px;
  }
`; 